import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/feature/Layout';
import { fetchQuestions } from '../../repositories/questions';
import type { Question } from '../../types/questions';
import { useSEO, generateWebPageSchema } from '../../utils/seo';

export default function QuestionsPage() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // フィルター用のstate
  const [selectedSubject, setSelectedSubject] = useState<string>('すべて');
  const [selectedGrade, setSelectedGrade] = useState<string>('すべて');
  const [keyword, setKeyword] = useState<string>('');

  // SEO設定
  useSEO({
    title: '学習相談ひろば｜キョウコレ',
    description: '全国の居場所・子どもたちから届いた「勉強のつまずき」と、それを一緒に考えた記録です。教科・学年別に学習相談を検索できます。',
    keywords: '学習相談,勉強の悩み,つまずき,学習支援,質問,回答,教科別相談',
    canonical: (import.meta.env.VITE_SITE_URL || 'https://example.com') + '/questions',
    jsonLd: generateWebPageSchema(
      '学習相談ひろば｜キョウコレ',
      '全国の居場所・子どもたちから届いた「勉強のつまずき」と、それを一緒に考えた記録です。教科・学年別に学習相談を検索できます。',
      '/questions'
    ),
  });

  useEffect(() => {
    loadQuestions();
  }, [selectedSubject, selectedGrade, keyword]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const filters: { subject?: string; grade?: string; keyword?: string } = {};
      
      if (selectedSubject !== 'すべて') {
        filters.subject = selectedSubject;
      }
      if (selectedGrade !== 'すべて') {
        filters.grade = selectedGrade;
      }
      if (keyword.trim()) {
        filters.keyword = keyword.trim();
      }

      const data = await fetchQuestions(filters);
      setQuestions(data);
    } catch (err) {
      console.error('Failed to load questions:', err);
      setError('学習相談の読み込みに失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  // 教科の一覧
  const subjects = ['すべて', '算数・数学', '国語', '英語', '理科', '社会', 'その他'];

  // 学年の一覧
  const grades = ['すべて', '小1', '小2', '小3', '小4', '小5', '小6', '中1', '中2', '中3', '高校生', 'その他'];

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getExcerpt = (text: string | null, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Layout>
      {/* ヒーローセクション */}
      <div className="bg-gradient-to-br from-teal-50 to-blue-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            学習相談ひろば
          </h1>
          <p className="text-lg text-gray-700 mb-6">
            全国の居場所・子どもたちから届いた「勉強のつまずき」と、それを一緒に考えた記録です。
          </p>
          <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 max-w-2xl mx-auto">
            <p className="text-sm text-gray-600 mb-2">
              <i className="ri-information-line mr-1"></i>
              個人が特定されないよう配慮して編集しています
            </p>
            <p className="text-sm text-gray-600">
              <i className="ri-alert-line mr-1"></i>
              緊急の悩みや健康・安全に関わる相談は、専門機関にご相談ください
            </p>
          </div>

          {/* このページの使い方 */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 max-w-3xl mx-auto mt-4">
            <h2 className="text-sm font-semibold text-gray-900 mb-3">
              <i className="ri-guide-line mr-1"></i>
              このページの使い方
            </h2>
            <p className="text-sm text-gray-700 mb-3">
              ここでは、全国の居場所や支援の現場から寄せられた「学習に関するお困りごと」を紹介しています。
              自分と似たケースを探したり、「うちの子／うちの居場所でも起こりそう」と感じるポイントを見つけるのに役立ててください。
            </p>
            <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
              <li>気になる相談を読む（教科・学年・キーワードでしぼりこみ）</li>
              <li>似たケースの工夫や、活用レシピもチェック</li>
              <li>自分の相談を投稿して、整理したいときは「学習の相談を投稿する」から</li>
            </ul>
          </div>

          {/* 投稿ボタン */}
          <div className="bg-white/80 backdrop-blur-sm rounded-lg p-4 max-w-2xl mx-auto mt-4">
            <p className="text-sm text-gray-700 mb-3">
              子どもとの関わりや学習のつまずきについて、オンラインで相談したい内容を投稿できます。
            </p>
            <button
              type="button"
              onClick={() => navigate('/questions/new')}
              className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer font-medium"
            >
              <i className="ri-edit-line mr-2"></i>
              学習の相談を投稿する
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* フィルターエリア */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            <i className="ri-filter-line mr-2"></i>
            絞り込み検索
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* 教科フィルター */}
            <div>
              <label htmlFor="subject-filter" className="block text-sm font-medium text-gray-700 mb-2">
                教科
              </label>
              <select
                id="subject-filter"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                {subjects.map((subject) => (
                  <option key={subject} value={subject}>
                    {subject}
                  </option>
                ))}
              </select>
            </div>

            {/* 学年フィルター */}
            <div>
              <label htmlFor="grade-filter" className="block text-sm font-medium text-gray-700 mb-2">
                学年
              </label>
              <select
                id="grade-filter"
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 cursor-pointer"
              >
                {grades.map((grade) => (
                  <option key={grade} value={grade}>
                    {grade}
                  </option>
                ))}
              </select>
            </div>

            {/* キーワード検索 */}
            <div>
              <label htmlFor="keyword-search" className="block text-sm font-medium text-gray-700 mb-2">
                キーワード検索
              </label>
              <input
                id="keyword-search"
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="タイトル・内容で検索"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>

        {/* ローディング */}
        {loading && (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
            <p className="text-gray-600">読み込み中…</p>
          </div>
        )}

        {/* エラー */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* 結果なし */}
        {!loading && !error && questions.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>
            <p className="text-gray-600 text-lg mb-2">
              条件に一致する相談が見つかりませんでした
            </p>
            <p className="text-sm text-gray-500">
              フィルターを変更して再度お試しください
            </p>
          </div>
        )}

        {/* 相談一覧 */}
        {!loading && !error && questions.length > 0 && (
          <>
            <div className="mb-4 text-sm text-gray-600">
              {questions.length}件の相談が見つかりました
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {questions.map((question) => (
                <div
                  key={question.id}
                  onClick={() => navigate(`/questions/${question.slug || question.id}`)}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                >
                  {/* ピックアップバッジ */}
                  {question.is_featured && (
                    <div className="mb-3">
                      <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded whitespace-nowrap">
                        <i className="ri-star-fill mr-1"></i>
                        ピックアップ
                      </span>
                    </div>
                  )}

                  {/* メタ情報 */}
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {question.subject && (
                      <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs font-medium rounded whitespace-nowrap">
                        {question.subject}
                      </span>
                    )}
                    {question.grade && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded whitespace-nowrap">
                        {question.grade}
                      </span>
                    )}
                  </div>

                  {/* タイトル */}
                  <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                    {question.title}
                  </h3>

                  {/* 本文抜粋 */}
                  <p className="text-sm text-gray-600 mb-3 line-clamp-3">
                    {getExcerpt(question.body_public || question.body_raw || '', 80)}
                  </p>

                  {/* タグ */}
                  {question.tags && question.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {question.tags.slice(0, 3).map((tag, idx) => (
                        <span
                          key={idx}
                          className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded whitespace-nowrap"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* 投稿日時 */}
                  <div className="flex items-center justify-between text-xs text-gray-500 pt-3 border-t border-gray-100">
                    <span>
                      <i className="ri-calendar-line mr-1"></i>
                      {formatDate(question.created_at)}
                    </span>
                    {question.answer && (
                      <span className="text-teal-600 font-medium">
                        <i className="ri-chat-check-line mr-1"></i>
                        回答あり
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ボラタスへの導線 */}
        <div className="mt-12 bg-gradient-to-r from-teal-500 to-blue-500 rounded-lg p-8 text-white text-center">
          <h2 className="text-2xl font-bold mb-3">
            オンラインで学習をサポート
          </h2>
          <p className="text-lg mb-6 opacity-90">
            ボラタスアプリで、ボランティアとつながって一緒に学びませんか？
          </p>
          <a
            href="https://voluntas.jp"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 bg-white text-teal-600 font-semibold rounded-lg hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-external-link-line mr-2"></i>
            ボラタスアプリへ
          </a>
        </div>
      </div>
    </Layout>
  );
}
